/**
 * AssetCard - compact card for brand assets with context menu,
 * category badge, read-only tag pills, and hover-revealed actions.
 * Follows the same group/card hover pattern as AudioMediaCard.
 */

import React, { useState } from "react";
import {
  TrashIcon,
  DownloadIcon,
  EditIcon,
  ImageIcon,
} from "@/components/icons";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { ContextMenu } from "@/components/MediaCard/ContextMenu";
import type { MenuItem } from "@/components/MediaCard/ContextMenu";
import { TagEditModal } from "./TagEditModal";
import type { ClientAsset } from "../types";
import { MIN_ROUND_SIZE } from "../types";

interface AssetCardProps {
  asset: ClientAsset;
  clientId: string;
  onDelete: (id: number) => void;
  onTagsChange: (id: number, tags: string[]) => void;
  onGenerateSizes?: (id: number) => void;
}

export const AssetCard: React.FC<AssetCardProps> = ({
  asset,
  clientId,
  onDelete,
  onTagsChange,
  onGenerateSizes,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [imgError, setImgError] = useState(false);

  const thumbnailUrl = asset.thumbnailUrl;

  const canGenerateSizes =
    asset.category === "round" &&
    asset.width !== null &&
    asset.width >= MIN_ROUND_SIZE;

  const dimensionLabel =
    asset.width && asset.height
      ? `${asset.width}x${asset.height}`
      : null;

  const fileSizeLabel = formatFileSize(asset.fileSize);

  const handleDownloadOriginal = async () => {
    if (!thumbnailUrl) return;
    try {
      const response = await fetch(thumbnailUrl);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = asset.originalFilename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Failed to download asset:", err);
    }
  };

  const menuItems: MenuItem[] = [
    {
      id: "edit-tags",
      label: "Edit Tags",
      icon: <EditIcon className="w-4 h-4" />,
      onClick: () => setShowTagModal(true),
    },
    {
      id: "download",
      label: "Download Original",
      icon: <DownloadIcon className="w-4 h-4" />,
      onClick: handleDownloadOriginal,
    },
    ...(canGenerateSizes && onGenerateSizes
      ? [
          {
            id: "generate-sizes",
            label: "Generate Sizes",
            icon: <DownloadIcon className="w-4 h-4" />,
            onClick: () => onGenerateSizes(asset.id),
          },
        ]
      : []),
    {
      id: "delete",
      label: "Delete",
      icon: <TrashIcon className="w-4 h-4" />,
      onClick: () => setShowDeleteConfirm(true),
      variant: "danger" as const,
    },
  ];

  const categoryBadge = asset.category === "round"
    ? { label: "Profile", color: "bg-purple-100 text-purple-700" }
    : { label: "Freestyle", color: "bg-teal-100 text-teal-700" };

  return (
    <>
      <div className="group/card bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow">
        {/* Thumbnail */}
        <div className="relative aspect-square bg-gray-800 flex items-center justify-center">
          {imgError || !thumbnailUrl ? (
            <ImageIcon className="w-8 h-8 text-gray-500" />
          ) : (
            <img
              src={thumbnailUrl}
              alt={asset.originalFilename}
              className="w-full h-full object-contain"
              onError={() => setImgError(true)}
            />
          )}

          {/* Category badge top-left */}
          <div className="absolute top-1.5 left-1.5">
            <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${categoryBadge.color}`}>
              {categoryBadge.label}
            </span>
          </div>

          {/* Context menu top-right, shown on hover */}
          <div
            className="absolute top-1.5 right-1.5 opacity-0 group-hover/card:opacity-100 transition-opacity"
            data-interactive="true"
          >
            <ContextMenu items={menuItems} />
          </div>
        </div>

        {/* Info */}
        <div className="px-2 py-1.5 space-y-0.5">
          <p
            className="text-xs font-medium text-gray-900 truncate"
            title={asset.originalFilename}
          >
            {asset.originalFilename}
          </p>

          <div className="flex items-center gap-1 text-[10px] text-gray-500">
            {dimensionLabel && <span>{dimensionLabel}</span>}
            {dimensionLabel && fileSizeLabel && <span>-</span>}
            {fileSizeLabel && <span>{fileSizeLabel}</span>}
          </div>

          {/* Read-only tag pills */}
          {asset.tags.length > 0 && (
            <div className="flex flex-wrap gap-0.5 pt-0.5">
              {asset.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-1.5 py-px bg-blue-50 text-blue-600 text-[10px] rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          onDelete(asset.id);
        }}
        title="Delete Asset"
        message={`Delete "${asset.originalFilename}"? This cannot be undone.`}
        confirmText="Delete"
        variant="danger"
      />

      <TagEditModal
        isOpen={showTagModal}
        onClose={() => setShowTagModal(false)}
        selectedTags={asset.tags}
        onChange={(tags) => onTagsChange(asset.id, tags)}
      />
    </>
  );
};

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
